/**
 * Azure Application Insights — browser SDK initialization.
 *
 * Shares the backend-ecotic App Insights resource; differentiated via
 * ai.cloud.role = "ecotic-frontend" so FE and BE telemetry stay separable
 * in the same Azure resource.
 *
 * Activation gate: IS_PRODUCTION && connectionString present.
 * All exports are no-op when the gate fails (dev local, missing secret) —
 * callers never need to check.
 *
 * Cookie-urile (`ai_user`, `ai_session`, `ai_authUser`) pornesc OPRITE si se aprind doar la acordul
 * pentru categoria `analytics` din bannerul de cookie-uri (`core/consent/cookie-consent.ts`), prin
 * `aplicaAcordAnaliza`. Telemetria in sine (erori, cereri, pagini) merge si fara ele, doar ca fara
 * identificator stabil de utilizator/sesiune.
 */

import {
    ApplicationInsights,
    type ITelemetryItem,
} from "@microsoft/applicationinsights-web";
import { ReactPlugin } from "@microsoft/applicationinsights-react-js";
import type React from "react";

import { IS_PRODUCTION } from "@/constants/config";
import { detecteazaBrowserInApp } from "@/shared/utils/in-app-browser";

import { esteZgomotTerta, sanitizeEnvelope } from "./envelope";
import { getPostHogReplayUrl, getPostHogSessionId } from "./posthog";
import { parseSamplingPercentage } from "./sampling";

const CLOUD_ROLE = "ecotic-frontend";
const connectionString = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING as
    | string
    | undefined;

let appInsightsInstance: ApplicationInsights | null = null;
let reactPluginInstance: ReactPlugin | null = null;
let currentRole: string | undefined;
let analizaAcceptata = false;

if (IS_PRODUCTION && connectionString) {
    const reactPlugin = new ReactPlugin();
    const ai = new ApplicationInsights({
        config: {
            connectionString,
            extensions: [reactPlugin],
            enableCorsCorrelation: true,
            enableRequestHeaderTracking: true,
            enableResponseHeaderTracking: true,
            enableAutoRouteTracking: true,
            autoTrackPageVisitTime: true,
            disableAjaxTracking: false,
            disableFetchTracking: false,
            enableUnhandledPromiseRejectionTracking: true,
            // Acelasi procent ca pe backend, altfel legatura AJAX -> request se rupe.
            // Vezi `sampling.ts`.
            samplingPercentage: parseSamplingPercentage(
                import.meta.env.VITE_APPINSIGHTS_SAMPLING_PERCENTAGE as string | undefined
            ),
            loggingLevelConsole: 0,
            loggingLevelTelemetry: 1,
            // Fara cookie-uri pana la acord (ePrivacy): le aprinde `aplicaAcordAnaliza`.
            cookieCfg: { enabled: false },
        },
    });

    ai.loadAppInsights();

    // Containerul nu se schimba pe durata paginii: se citeste o data, nu la fiecare envelope.
    const inAppBrowser = detecteazaBrowserInApp(navigator.userAgent);

    ai.addTelemetryInitializer((envelope: ITelemetryItem) => {
        // PRIMUL lucru: zgomotul injectat de aplicatia-gazda (puntea nativa a Facebook, «Script
        // error.» de pe alta origine) nu pleaca deloc — un initializer care intoarce `false`
        // opreste trimiterea. Decizia e pe continutul erorii, nu pe container: vezi `envelope.ts`.
        if (esteZgomotTerta(envelope)) return false;

        envelope.tags = envelope.tags || {};
        envelope.tags["ai.cloud.role"] = CLOUD_ROLE;

        envelope.data = envelope.data || {};

        if (currentRole) {
            envelope.data.userRole = currentRole;
        }

        // Dimensiunile ecranului NU sunt trimise implicit de SDK-ul JS, deci distributia de
        // rezolutii nu e recuperabila retroactiv (vezi docs/knowledge/device-uri-2025-2026.md).
        // Se trimit de aici ca sa existe pentru editia urmatoare.
        envelope.data.screenWidth = window.screen.width;
        envelope.data.screenHeight = window.screen.height;
        envelope.data.viewportWidth = window.innerWidth;
        envelope.data.devicePixelRatio = window.devicePixelRatio;
        envelope.data.touch = navigator.maxTouchPoints > 0;
        // `facebook` / `instagram` / `tiktok` / `none`: erorile NOASTRE din WebView-ul unei aplicatii
        // raman in telemetrie si se pot filtra dupa container.
        envelope.data.inAppBrowser = inAppBrowser;

        const posthogSessionId = getPostHogSessionId();
        if (posthogSessionId) {
            envelope.data.posthogSessionId = posthogSessionId;
        }

        // Redactarea tokenului de colaborator (baseData + tags) si numele paginii pe ruta.
        sanitizeEnvelope(envelope);
    });

    appInsightsInstance = ai;
    reactPluginInstance = reactPlugin;
}

export const appInsights = appInsightsInstance;
export const reactPlugin = reactPluginInstance;

export function setAuthenticatedUser(userId: string, role?: string): void {
    currentRole = role;
    // `storeInCookie` urmeaza acordul: fara el, `ai_authUser` nu se scrie.
    appInsightsInstance?.setAuthenticatedUserContext(userId, undefined, analizaAcceptata);
}

/**
 * Aprinde sau stinge cookie-urile App Insights dupa alegerea din banner. Chemata la fiecare
 * incarcare (acordul salvat) si la fiecare schimbare. Stergerea cookie-urilor deja puse o face
 * bannerul (`autoClear`); aici doar oprim scrierea lor de acum inainte.
 */
export function aplicaAcordAnaliza(acceptat: boolean): void {
    analizaAcceptata = acceptat;
    appInsightsInstance?.getCookieMgr().setEnabled(acceptat);
}

export function clearAuthenticatedUser(): void {
    currentRole = undefined;
    appInsightsInstance?.clearAuthenticatedUserContext();
}

export function trackException(
    error: Error,
    errorInfo?: React.ErrorInfo
): void {
    const properties: Record<string, string> = {};
    if (errorInfo?.componentStack) properties.componentStack = errorInfo.componentStack;
    const replayUrl = getPostHogReplayUrl();
    if (replayUrl) properties.posthogReplayUrl = replayUrl;

    appInsightsInstance?.trackException({
        exception: error,
        properties: Object.keys(properties).length ? properties : undefined,
    });
}
